/**
 * Guided tours for the Teams app (Bill 2026-10-07: "a runbook or walkthrough
 * for each of the roles so that they are not going into everything blind").
 *
 * SCOPE DISCIPLINE (same rule as Help): every step points at a surface that
 * is live for the Phase 1 pilot. Nothing here describes a hidden module. The
 * tours never press a button for the person - in particular they never open
 * New Account, because that creates a real cart. The account-opening
 * walkthrough instead starts the first time the person opens an application
 * themselves (startOnPath).
 *
 * Order matters: the first tour matching someone's roles is the one that
 * starts on first open (Officer before Supervisor/Manager before Executive);
 * the others wait in Help → Guided tours. Bump a tour's version when its
 * steps change materially so people see the new one once.
 */
import type { TourDef } from '@retail-ops/guided-tour';

const OFFICER_ROLES = ['Officer', 'Admin', 'SuperAdmin'];

export const ROP_TOURS: ReadonlyArray<TourDef> = [
  {
    id: 'rop-officer',
    version: 1,
    title: 'Officer: finding your way around',
    summary: 'Home, finding a customer, your carts, documents, and where to get help',
    roles: OFFICER_ROLES,
    steps: [
      {
        route: '/',
        title: 'Welcome to Retail Operations',
        body: (
          <>
            <p>During the pilot this app does one job end to end: open a deposit account for a personal customer, capture the documents, book it to Core Director, and hand over the debit card.</p>
            <p>Pilot products are <strong>Individual, Joint and Minor</strong> only. This tour takes about two minutes. Use the arrow keys or the buttons; you can leave at any time and take it again from Help.</p>
          </>
        ),
      },
      {
        target: 'mode-banner',
        title: 'Which mode you are in',
        body: (
          <>
            <p>This strip says how the app is running for you. <strong>Test mode</strong> (purple) is the real Core Director, but only test customers: last name <strong>Test</strong> with a tax ID starting <strong>666</strong>, or a CIF starting <strong>000</strong>. Real customers are hidden and refused.</p>
            <p>No strip means you are working live. Your mode is set by an administrator.</p>
          </>
        ),
      },
      {
        target: 'home-start-cart',
        title: 'Start a new account',
        body: (
          <>
            <p><strong>New Account</strong> starts an application. It has three stages: <strong>People</strong>, <strong>Products</strong>, then <strong>Review &amp; Submit</strong>, where the signing and the booking to Core Director happen.</p>
            <p>The first time you open an application, a short walkthrough of that screen starts by itself.</p>
          </>
        ),
      },
      {
        target: 'home-find-customer',
        title: 'Look someone up',
        body: <p><strong>Find Customer</strong> searches by name, SSN or CIF and shows the full record and their accounts. Search before you add anyone, so existing customers are found rather than created twice.</p>,
      },
      {
        target: 'home-my-carts',
        title: 'Your applications in progress',
        body: <p>Every application you have started and not finished. Click one to pick up where you left off. An application is saved as you go, so closing the app never loses it.</p>,
      },
      {
        target: 'home-online-apps',
        title: 'Online applications',
        body: (
          <>
            <p>Applications customers started online wait here to be claimed. Claiming one makes it yours, with the applicant&rsquo;s details already filled in. Check their work rather than retyping it.</p>
            <p>Bringing new applications in from the old online system is <strong>paused</strong> until pilot testing is complete.</p>
          </>
        ),
      },
      {
        target: 'topbar-branch',
        title: 'Your branch',
        body: <p>Work books to the branch shown here. Covering another location today? Click it to change the branch for the day; it goes back to your own branch tomorrow.</p>,
      },
      {
        target: 'rail-rdl',
        route: '/rdl',
        title: 'Documents',
        body: <p>Everything on file for your customers: IDs, signed PDFs, uploads and notices.</p>,
      },
      {
        target: 'docs-tabs',
        title: 'Two ways to look',
        body: <p><strong>My Customers</strong> groups documents by customer. <strong>By Cart</strong> shows what is attached to an application in progress. Click any document for a preview.</p>,
      },
      {
        target: 'docs-upload',
        title: 'Add a document',
        body: <p><strong>Upload document</strong> files a scan or PDF against a customer or an application.</p>,
      },
      {
        target: 'topbar-notifications',
        route: '/',
        title: 'Notifications',
        body: <p>The bell collects things that need your attention. The number shows what you have not read yet.</p>,
      },
      {
        target: 'topbar-user',
        title: 'You',
        body: <p>Your name and role. If you also have an Admin, Risk Management or SysOps role, the links to those panels are here; they open in your browser, not in Teams.</p>,
      },
      {
        target: 'topbar-help',
        title: 'Help',
        body: <p>Short how-to guides, what common messages mean, and <strong>About this app</strong> (your build and whether Core Director, LexisNexis and the scanner are reachable). You can take this tour again from there.</p>,
      },
      {
        target: 'report-problem',
        title: 'Report a problem or suggest a change',
        body: (
          <>
            <p>The lifebuoy in the bottom-left corner, on every screen. Tell us what you were doing and what happened; a picture of your screen and the recent activity go with it, so nobody has to ask you to do it again.</p>
            <p>Suggestions and change requests go to management for review first.</p>
          </>
        ),
      },
      {
        title: 'You are ready',
        body: <p>Start with <strong>New Account</strong> or <strong>Find Customer</strong> on Home. The account-opening walkthrough starts when you open your first application.</p>,
      },
    ],
  },
  {
    id: 'rop-application',
    version: 1,
    title: 'Officer: opening an account',
    summary: 'The application screen - people, products, review, signing and booking',
    launchHint: 'Starts by itself the first time you open an application. Open one first to see each control.',
    roles: OFFICER_ROLES,
    startOnPath: /^\/rao\/(?!new(?:\/|$))[^/]+\/?$/,
    steps: [
      {
        title: 'This is an application',
        body: (
          <>
            <p>Three stages, in order: <strong>People</strong> on the account, the <strong>Products</strong> they are opening with each person&rsquo;s role, then <strong>Review &amp; Submit</strong> - where you check everything, collect signatures and book it to Core Director.</p>
            <p>Nothing reaches Core Director until that last step.</p>
          </>
        ),
      },
      {
        target: 'cart-person-search',
        title: 'Find each person first',
        body: (
          <>
            <p>Search by SSN, name or CIF number. If they are already a customer, pick them - their details come from Core Director.</p>
            <p>In Test mode only test customers are found: last name Test with a 666 tax ID, or a 000 CIF.</p>
          </>
        ),
      },
      {
        target: 'cart-new-person',
        title: 'Someone new',
        body: <p>Not found? <strong>+ New Person</strong> captures them: identity, ID document, address and contact details. The identity checks run from there.</p>,
      },
      {
        target: 'cart-sidebar',
        title: 'The application at a glance',
        body: <p>Who is on it, what is being opened, and what is still needed. It updates as you work.</p>,
      },
      {
        target: 'cart-footer',
        title: 'Moving through the stages',
        body: (
          <>
            <p><strong>Continue</strong> moves to the next stage (it waits until at least one person is added). <strong>Back</strong> goes back a stage.</p>
            <p><strong>Save &amp; close</strong> leaves the application where it is; the Resume chip in the top bar and My Carts on Home bring you back.</p>
          </>
        ),
      },
      {
        title: 'Signing, booking and the debit card',
        body: (
          <>
            <p>On <strong>Review &amp; Submit</strong>, collect the signatures. When everyone has signed, commit to book the account in Core Director.</p>
            <p>If a debit card was ordered, print it at the Entrust station, then come back to the finished application and <strong>activate</strong> it there. The app checks Core Director and tells you if the card is not printed yet.</p>
          </>
        ),
      },
      {
        title: 'Stuck?',
        body: <p>Help → How do I… covers the common jobs. If something looks wrong, use the lifebuoy in the bottom-left corner while you are still on the screen - the picture it takes shows exactly what you saw.</p>,
      },
    ],
  },
  {
    id: 'rop-supervisor',
    version: 1,
    title: 'Supervisor and Manager: keeping an eye on the work',
    summary: 'Your team’s applications, reassigning work, documents',
    roles: ['Supervisor', 'Manager'],
    steps: [
      {
        route: '/',
        title: 'Welcome',
        body: <p>As a Supervisor or Manager you see the work in flight and the documents, and you can move an application to another officer. Opening accounts is on the New Acct tab for officers.</p>,
      },
      {
        target: 'mode-banner',
        title: 'Which mode you are in',
        body: <p><strong>Test mode</strong> (purple) means real Core Director, test customers only (last name Test, tax ID starting 666, or a CIF starting 000). No strip means live.</p>,
      },
      {
        target: 'home-my-carts',
        title: 'Applications in progress',
        body: <p>Tick <strong>Show all</strong> to see more than your own. <strong>See all</strong> opens the full list, where each application can be <strong>reassigned</strong> to another officer - for example when someone is out.</p>,
      },
      {
        target: 'home-online-apps',
        title: 'Online applications',
        body: <p>Applications customers started online, waiting for an officer to claim. Bringing new ones in from the old online system is paused until pilot testing is complete.</p>,
      },
      {
        target: 'rail-rdl',
        route: '/rdl',
        title: 'Documents',
        body: <p>Documents by customer and by application. Managers also get the <strong>Library</strong> (search everything by filename, CIF, type and date) and can delete a misfiled document from its preview.</p>,
      },
      {
        target: 'docs-tabs',
        title: 'Views',
        body: <p>Switch between the views here. Click any document for a preview.</p>,
      },
      {
        target: 'topbar-help',
        route: '/',
        title: 'Help',
        body: <p>How-to guides, what common messages mean, and this tour again.</p>,
      },
      {
        target: 'report-problem',
        title: 'Report a problem or suggest a change',
        body: <p>The lifebuoy, bottom-left, on every screen. A picture of the screen and the recent activity go with your report. Suggestions go to management for review first.</p>,
      },
    ],
  },
  {
    id: 'rop-executive',
    version: 1,
    title: 'Executive: a quick look',
    summary: 'What is in flight, and how to raise something',
    roles: ['Executive'],
    steps: [
      {
        route: '/',
        title: 'Welcome',
        body: <p>For the pilot, Home shows the applications in flight. Opening accounts and documents are on the officer and manager tabs.</p>,
      },
      {
        target: 'home-my-carts',
        title: 'Applications in progress',
        body: <p>Tick <strong>Show all</strong> to see every application in flight, not only your own.</p>,
      },
      {
        target: 'topbar-user',
        title: 'You',
        body: <p>Your name and role, plus links to any panels your role can open.</p>,
      },
      {
        target: 'report-problem',
        title: 'Report a problem or suggest a change',
        body: <p>The lifebuoy, bottom-left. Suggestions and change requests go to management for review before development.</p>,
      },
    ],
  },
];
